import { PersonAdd } from "@mui/icons-material";
import { Button, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  UrlPathSegment,
  UrlPathSegmentBuilderAi,
  UrlPathSegmentBuilderCsv,
  UrlPathSegmentBuilderEasy,
} from "../../../api/url";
import MenuLayout from "../../app/MenuLayout";
import ImsPaperHead from "../../atoms/ImsPaperHead";

const FILE_UPLOAD = "fileUpload";
const AI_SEGMENTS = "aiSegments";
const EASY_FILTERS = "easyFilters";

const redirectMap = new Map([
  [FILE_UPLOAD, UrlPathSegmentBuilderCsv],
  [AI_SEGMENTS, UrlPathSegmentBuilderAi],
  [EASY_FILTERS, UrlPathSegmentBuilderEasy],
]);

const SegmentsBuilder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [datasourceSelection, setDatasourceSelection] = useState<string>(searchParams.get("selection") || FILE_UPLOAD);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Update URL when selection changes
    if (datasourceSelection) {
      setSearchParams({ selection: datasourceSelection });
    } else {
      setSearchParams({});
    }
  }, [datasourceSelection, setSearchParams]);

  const handleCardClick = (selection: string) => {
    setDatasourceSelection(selection);
    const nextUrl = redirectMap.get(selection) ?? "";

    if (nextUrl) {
      navigate(nextUrl);
    }
  };

  return (
    <div>
      <MenuLayout headline={t("segmentBuilder.title")}>
        <Paper style={{ padding: theme.spacing(3), marginTop: theme.spacing(4), marginBottom: theme.spacing(4) }}>
          <ImsPaperHead text={t("segmentBuilder.header")} />
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                style={{ marginBottom: theme.spacing(2), color: "black", fontWeight: "bold" }}
              >
                {t("segmentBuilder.selectionInstruction")}
              </Typography>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item sm={4}>
                <Paper
                  onClick={() => handleCardClick(FILE_UPLOAD)}
                  elevation={datasourceSelection === FILE_UPLOAD ? 2 : 1}
                  sx={{
                    padding: theme.spacing(2),
                    backgroundColor: datasourceSelection === FILE_UPLOAD ? "#EAEAEA" : "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    height: "100%",
                    border: datasourceSelection === FILE_UPLOAD ? `1px solid gray` : "none",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#E0E0E0",
                      elevation: 2,
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: theme.spacing(2),
                      borderRight: `1px solid gray`,
                      marginRight: theme.spacing(2),
                    }}
                  >
                    <PersonAdd />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Typography variant="h6">{t("segmentBuilder.fileUpload")}</Typography>
                    <Typography variant="body2">
                      {t("segmentBuilder.fileUploadDescription").split("\n\n")[0]}
                    </Typography>
                  </div>
                </Paper>
              </Grid>
              <Grid item sm={4}>
                <Paper
                  onClick={() => handleCardClick(AI_SEGMENTS)}
                  elevation={datasourceSelection === AI_SEGMENTS ? 2 : 1}
                  sx={{
                    padding: theme.spacing(2),
                    backgroundColor: datasourceSelection === AI_SEGMENTS ? "#EAEAEA" : "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    height: "100%",
                    border: datasourceSelection === AI_SEGMENTS ? `1px solid gray` : "none",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#E0E0E0",
                      elevation: 2,
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: theme.spacing(2),
                      borderRight: `1px solid ${theme.palette.divider}`,
                      marginRight: theme.spacing(2),
                    }}
                  >
                    <PersonAdd />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Typography variant="h6">{t("segmentBuilder.aiSegments")}</Typography>
                    <Typography variant="body2">{t("segmentBuilder.aiSegmentsDescription")}</Typography>
                  </div>
                </Paper>
              </Grid>
              <Grid item sm={4}>
                <Paper
                  onClick={() => handleCardClick(EASY_FILTERS)}
                  elevation={datasourceSelection === EASY_FILTERS ? 2 : 1}
                  sx={{
                    padding: theme.spacing(2),
                    backgroundColor: datasourceSelection === EASY_FILTERS ? "#EAEAEA" : "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    height: "100%",
                    border: datasourceSelection === EASY_FILTERS ? `1px solid gray` : "none",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#E0E0E0",
                      elevation: 2,
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: theme.spacing(2),
                      borderRight: `1px solid ${theme.palette.divider}`,
                      marginRight: theme.spacing(2),
                    }}
                  >
                    <PersonAdd />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Typography variant="h6">{t("segmentBuilder.easyFilters")}</Typography>
                    <Typography variant="body2">{t("adHocSegments.description")}</Typography>
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing(2) }}>
          <Button color="secondary" variant="contained" component={Link} to={UrlPathSegment} style={{ minWidth: 150 }}>
            {t("common.cancel")}
          </Button>
        </div>
      </MenuLayout>
    </div>
  );
};

export default SegmentsBuilder;
